import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import {
  loginApi,
  guestLoginApi,
  signUpApi,
  requestPasswordResetApi,
  changePasswordApi,
  verifyEmailTokenApi,
  checkPasswordResetTokenApi,
  confirmPasswordResetApi,
  logoutApi,
  type LoginCredentials,
  type SignUpData,
  type ResetPasswordData,
  type ChangePasswordData,
  type TokenResponse,
} from '../authApi'
import LogTool from '../../logger/logTools'
import { type APIRegister, type APIV1AuthEmailRequestCheckTokenCreate200Response } from '../../generated-types'

const log = new LogTool({ context: 'AuthMutations', enable: true, logLevel: 'warn' })

/**
 * Hook to login user
 */
export function useLogin(): UseMutationResult<TokenResponse, Error, LoginCredentials> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: loginApi,
    onSuccess: async () => {
      // Clear all cached queries to prevent leaking data between accounts
      queryClient.clear()
      await queryClient.invalidateQueries({ queryKey: ['users', 'self'] })
      log.info('Login mutation successful')
    },
    onError: (error) => {
      log.error('Login failed:', error)
    },
  })
}

/**
 * Hook to guest login
 */
export function useGuestLogin(): UseMutationResult<TokenResponse, Error, LoginCredentials> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: guestLoginApi,
    onSuccess: async () => {
      // Clear cache so guest users never see stale data
      queryClient.clear()
      await queryClient.invalidateQueries({ queryKey: ['users', 'self'] })
      log.info('Guest login mutation successful')
    },
    onError: (error) => {
      log.error('Guest login failed:', error)
    },
  })
}

/**
 * Hook to sign up new user
 */
export function useSignUp(): UseMutationResult<APIRegister, Error, SignUpData> {
  return useMutation({
    mutationFn: signUpApi,
    onSuccess: () => {
      log.info('Sign up successful')
    },
    onError: (error) => {
      log.error('Sign up failed:', error)
    },
  })
}

/**
 * Hook to request password reset
 */
export function useRequestPasswordReset(): UseMutationResult<APIV1AuthEmailRequestCheckTokenCreate200Response, Error, ResetPasswordData> {
  return useMutation({
    mutationFn: requestPasswordResetApi,
    onSuccess: () => {
      log.info('Password reset email sent')
    },
    onError: (error) => {
      log.error('Password reset request failed:', error)
    },
  })
}

/**
 * Hook to change password
 */
export function useChangePassword(): UseMutationResult<any, Error, ChangePasswordData> {
  return useMutation({
    mutationFn: changePasswordApi,
    onSuccess: () => {
      log.info('Password changed successfully')
    },
    onError: (error) => {
      log.error('Password change failed:', error)
    },
  })
}

/**
 * Hook to logout
 */
export function useLogout(): UseMutationResult<void, Error, void> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => {
      logoutApi()
      return Promise.resolve()
    },
    onSuccess: () => {
      // Clear all cached data on logout
      queryClient.clear()
      log.info('Logout successful, cache cleared')
    },
  })
}

/**
 * Hook to verify email token (for invite/signup)
 */
export function useVerifyEmailToken(): UseMutationResult<APIV1AuthEmailRequestCheckTokenCreate200Response, Error, { token: string; password?: string }> {
  return useMutation({
    mutationFn: verifyEmailTokenApi,
    onSuccess: () => {
      log.info('Email token verified successfully')
    },
    onError: (error) => {
      log.error('Email token verification failed:', error)
    },
  })
}

/**
 * Hook to check password reset token validity
 */
export function useCheckPasswordResetToken(): UseMutationResult<APIV1AuthEmailRequestCheckTokenCreate200Response, Error, string> {
  return useMutation({
    mutationFn: checkPasswordResetTokenApi,
    onSuccess: () => {
      log.info('Password reset token is valid')
    },
    onError: (error) => {
      log.error('Password reset token check failed:', error)
    },
  })
}

/**
 * Hook to confirm password reset
 */
export function useConfirmPasswordReset(): UseMutationResult<APIV1AuthEmailRequestCheckTokenCreate200Response, Error, { token: string; password: string }> {
  return useMutation({
    mutationFn: confirmPasswordResetApi,
    onSuccess: () => {
      log.info('Password reset confirmed successfully')
    },
    onError: (error) => {
      log.error('Password reset confirmation failed:', error)
    },
  })
}
